/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { Dimmer, Grid, Loader } from '@mairistem/composants';

import { Section, SectionHeader } from '@mairistem/administres-utils';

import type {
  Mention706702Data,
  Lieu,
  DateTroisChamps,
  LieuFrance,
  Prenom,
} from '@/types';

interface VerificationStepProps {
  mentionData: Mention706702Data | null;
  loading?: boolean;
}

const TYPE_ACTE_LABELS: Record<string, string> = {
  AN: 'Acte de naissance',
  AM: 'Acte de mariage',
  AD: 'Acte de décès',
};

const formatDate = (date: DateTroisChamps): string =>
  `${date.jour}/${date.mois}/${date.annee}`;

const formatLieu = (lieu: Lieu): string => {
  if (lieu.lieuFrance) {
    const { libelleCommuneFrance, departementFrance } = lieu.lieuFrance;
    return `${libelleCommuneFrance} (${departementFrance})`;
  }
  if (lieu.lieuEtranger) {
    const { libelleVilleEtranger, libellePaysEtranger } = lieu.lieuEtranger;
    return `${libelleVilleEtranger}, ${libellePaysEtranger}`;
  }
  return '—';
};

const formatLieuFrance = (lieu: LieuFrance): string =>
  `${lieu.libelleCommuneFrance} (${lieu.departementFrance})`;

const formatPrenoms = (prenoms: Prenom[]): string =>
  prenoms.map((p) => p.valeur).join(' ');

const Field = ({ label, value }: { label: string; value: string }) => (
  <div style={{ paddingLeft: '2em' }}>
    <b>{label}</b>
    {' : '}
    {value || '—'}
  </div>
);

const SubSectionHeader = ({ title }: { title: string }) => (
  <div style={{ paddingLeft: '1em' }}>
    <b>{title}</b>
  </div>
);

const VerificationStep = ({ mentionData, loading }: VerificationStepProps) => {
  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader content="Chargement des données..." />
      </Dimmer>
    );
  }

  if (!mentionData) {
    return (
      <Dimmer active inverted>
        <div style={{ color: '#333', textAlign: 'center' }}>
          <p>Impossible de récupérer les données de la mention.</p>
        </div>
      </Dimmer>
    );
  }

  const {
    mention,
    personneInteressee,
    acteCible,
    detailMention,
    texteMention,
  } = mentionData;

  const sectionTitle = mention?.intitule
    ? `MENTION ${mention.codificationAnts} - ${mention.intitule}`
    : 'MENTION 706702';

  return (
    <Section title={sectionTitle} style={{ width: 1100 }}>
      <Grid padded>
        <Grid.Row>
          <Grid.Column width={16}>
            <SectionHeader title="Acte cible" />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <Field
              label="Type d'acte"
              value={TYPE_ACTE_LABELS[acteCible.type] ?? acteCible.type}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <Field label="Nom" value={personneInteressee.nom} />
          </Grid.Column>
          <Grid.Column width={6}>
            <Field
              label="Prénoms"
              value={formatPrenoms(personneInteressee.prenoms)}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <Field label="Sexe" value={personneInteressee.sexe} />
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
              label="Date de naissance"
              value={formatDate(personneInteressee.naissance.date)}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <Field
              label="Lieu de naissance"
              value={formatLieu(personneInteressee.naissance.lieu)}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <SectionHeader title="Détail de l'événement relatif à la mention" />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <SubSectionHeader title="Événement" />
          </Grid.Column>
        </Grid.Row>
        {mention?.intitule && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Field label="Type" value={mention.intitule} />
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column width={6}>
            <Field
              label="Date"
              value={formatDate(detailMention.dateEvenement)}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Field
              label="Lieu"
              value={formatLieuFrance(detailMention.lieuEvenement)}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <SubSectionHeader title="Partenaire" />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <Field label="Nom" value={detailMention.partenaire.nom} />
          </Grid.Column>
          <Grid.Column width={8}>
            <Field
              label="Prénoms"
              value={formatPrenoms(detailMention.partenaire.prenoms)}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <Field
              label="Date de naissance"
              value={formatDate(detailMention.partenaire.naissance.date)}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Field
              label="Lieu de naissance"
              value={formatLieu(detailMention.partenaire.naissance.lieu)}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <SectionHeader title="Texte de la mention" />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <i style={{ padding: '1em' }}>{texteMention}</i>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Section>
  );
};

export default VerificationStep;
